// 3rd party imports
import { plainToClass } from 'class-transformer';
import { Subject } from 'rxjs';
// CC-Common imports
import { Utils } from 'cc-common';
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
// Constant imports
import { Constants } from '../../_constants/constants';
import { SideNavigationTree } from '../../_constants/side-navigation-tree';
import * as i0 from "@angular/core";
export class NavigationService {
    constructor() {
        this._subjectWorkspaceSubmenus = new Subject();
        this._subjectWorkspaceSelect = new Subject();
        this._subjectWorkspaceView = new Subject();
        this._subjectSearchInNavigations = new Subject();
        this._subjectTopMenuChanged = new Subject();
        this._topMenuVisible = false;
    }
    registerTrackUnsavedChanges(value) {
        this._trackUnsavedChanges = value;
    }
    unregisterTrackUnsavedChanges() {
        this._trackUnsavedChanges = null;
    }
    hasTrackUnsavedChanges() {
        return Utils.notNullAndDefined(this._trackUnsavedChanges);
    }
    trackUnsavedChanges() {
        return this._trackUnsavedChanges();
    }
    isTopMenuVisible() {
        return this._topMenuVisible;
    }
    getTopMenuStatus() {
        return this._subjectTopMenuChanged;
    }
    updateTopMenuStatus(flag) {
        this._topMenuVisible = flag;
        this._subjectTopMenuChanged.next(flag);
    }
    getBottomNavigationSource(result) {
        return this.getNavigationAccess(result, this.deepCopy(SideNavigationTree.bottomMenuItems), true);
    }
    getTopNavigationSource(result) {
        return this.getNavigationAccess(result, this.deepCopy(SideNavigationTree.topTreeItems));
    }
    getPropertyNavigationSource(result) {
        return this.getNavigationAccess(result, this.deepCopy(SideNavigationTree.propertyTreeItems));
    }
    getCommunityNavigationSource(result) {
        return this.getNavigationAccess(result, this.deepCopy(SideNavigationTree.communityTreeItems));
    }
    updateSearchedItem(value) {
        this._subjectSearchInNavigations.next(value);
    }
    getSearchedItem() {
        return this._subjectSearchInNavigations;
    }
    updateWorkspaceMenus(value) {
        this._subjectWorkspaceSubmenus.next(value);
    }
    onWorkspaceMenus() {
        return this._subjectWorkspaceSubmenus;
    }
    updateWorkspaceView(value) {
        this._subjectWorkspaceView.next(value);
    }
    onWorkspaceView() {
        return this._subjectWorkspaceView;
    }
    updateWorkspaceSelection(value) {
        this._subjectWorkspaceSelect.next(value);
    }
    onWorkspaceSelection() {
        return this._subjectWorkspaceSelect;
    }
    getNavigationAccess(menuActions, menuItems, includeDisabled = false) {
        const resultMenuItems = [];
        const disabledItems = this.hideNavigationItems(menuItems);
        disabledItems.forEach((menu) => {
            let isNavItemFound = false;
            const menuAction = menuActions.find((action) => Utils.equalsIgnoreCase(action.code, menu.name));
            // Set root level menu flags
            if (Utils.notNullAndDefined(menuAction)) {
                isNavItemFound = true;
                menu.hasAccess = true;
                menu.visible = true;
                menu.isInternal = menuAction.internalOnly;
                menu.isSpecificRole = menuAction.specificRoleOnly;
            }
            // Check and update root level based on sub menus
            if (Utils.notNullOrEmpty(menu.items)) {
                const subMenus = this.getNavigationAccess(menuActions, menu.items, includeDisabled);
                if (Utils.notNullOrEmpty(subMenus)) {
                    isNavItemFound = true;
                    menu.hasAccess = true;
                    menu.visible = true;
                    menu.items = subMenus;
                }
                else if (Utils.isTrue(isNavItemFound)) {
                    isNavItemFound = false;
                    menu.hasAccess = false;
                    menu.visible = false;
                    menu.items = [];
                }
            }
            const hasOnlyWorkspaceItem = Utils.equals(menu.items.length, 1) && Utils.isTrue(menu.items[0].isWorkspace);
            if ((Utils.isTrue(isNavItemFound) || Utils.isTrue(includeDisabled)) &&
                Utils.isFalse(hasOnlyWorkspaceItem)) {
                resultMenuItems.push(menu);
            }
        });
        return resultMenuItems;
    }
    // Side / Top Navigation Utility Methods
    resetSideNavItems(items, visible = true) {
        items.forEach((item) => {
            item.visible = this.isHomeItem(item) || this.isRootItem(item) || visible;
            item.expanded = false;
            item.selected = false;
            this.resetSideNavItems(item.items, false);
        });
    }
    showSideNavItems(items, selectedNode, recursive = true) {
        items.forEach((item) => {
            item.visible = true;
            item.selected = this.isSelectedItem(item, selectedNode);
            if (recursive && item.items.length > 0) {
                this.showSideNavItems(item.items, selectedNode);
            }
        });
    }
    hideSideNavItems(items, recursive = true) {
        items.forEach((item) => {
            item.visible = this.isHomeItem(item) || this.isRootItem(item);
            item.selected = false;
            if (recursive && item.items.length > 0) {
                this.hideSideNavItems(item.items);
            }
        });
    }
    expandSideNavItems(items, selectedNode, recursive = false, expandedItems = []) {
        items.forEach((item) => {
            item.visible = true;
            item.selected = this.isSelectedItem(item, selectedNode);
            item.expanded = item.items.length > 0;
            if (item.expanded) {
                expandedItems.push(item);
            }
            if (recursive && item.items.length > 0) {
                expandedItems = expandedItems.concat(this.expandSideNavItems(item.items, selectedNode, recursive, expandedItems));
            }
        });
        return expandedItems;
    }
    collapseSideNavItems(items, recursive = true, collapsedItems = []) {
        items.forEach((item) => {
            item.expanded = false;
            collapsedItems.push(item.id);
            if (recursive && item.items.length > 0) {
                collapsedItems = collapsedItems.concat(this.collapseSideNavItems(item.items, recursive, collapsedItems));
            }
        });
        return collapsedItems;
    }
    getSelectedSideNavItem(items, urlPath, selectedItem) {
        items.forEach((item) => {
            item.selected = !!item.path ? urlPath === this.getNormalizedPath(item.path) : false;
            if (item.selected) {
                selectedItem = item;
            }
            else {
                selectedItem = this.getSelectedSideNavItem(item.items, urlPath, selectedItem);
            }
        });
        return selectedItem;
    }
    isSelectedItem(item, selectedNode) {
        return !!selectedNode ? selectedNode.id === item.id : false;
    }
    isHomeItem(item) {
        return this.getNormalizedPath(item.path || '') === '/home';
    }
    isRootItem(item) {
        return !item.parentId;
    }
    getNormalizedPath(targetPath) {
        return targetPath.startsWith('/') ? targetPath : `/${targetPath}`;
    }
    getURLPath(currentPath, selectedContext) {
        let targetPath = currentPath;
        if (!!selectedContext) {
            const communityId = (selectedContext.communityId || '').toString();
            const customerId = selectedContext.customerId;
            if (targetPath.indexOf(communityId) !== -1) {
                targetPath = targetPath.replace(communityId, Constants.communityPathPattern);
                if (targetPath.indexOf(customerId) !== -1) {
                    targetPath = targetPath.replace(customerId, Constants.propertyPathPattern);
                }
            }
        }
        return this.getNormalizedPath(targetPath);
    }
    getParentItems(items, child, recursive = false, parentItems = [], topItems = []) {
        items.forEach((item) => {
            if (child && item.id === child.parentId) {
                parentItems.push(item);
                if (recursive) {
                    this.getParentItems(topItems, item, recursive, parentItems);
                }
            }
            this.getParentItems(item.items, child, recursive, parentItems);
        });
        return parentItems;
    }
    getSideNavItemById(items, id) {
        let menuItem = items.find((r) => r.id === id);
        if (!menuItem) {
            for (const item of items) {
                if (item.items.length > 0) {
                    menuItem = this.getSideNavItemById(item.items, id);
                    if (!!menuItem) {
                        break;
                    }
                }
            }
        }
        return menuItem;
    }
    selectWorkspaceItem(items, item) {
        let selectedNode = item;
        if (!!item) {
            const parent = this.getSideNavItemById(items, item.parentId);
            if (!!parent) {
                parent.items.forEach((r) => {
                    r.selected = r.id === item.id;
                    if (r.selected) {
                        selectedNode = r;
                    }
                });
            }
        }
        return selectedNode;
    }
    sortSideNavItemByName(menuItems) {
        const sortableItems = menuItems.filter((x) => !x.isWorkspace);
        if (sortableItems.length > 1) {
            sortableItems.sort((x, y) => {
                if (x.text > y.text) {
                    return 1;
                }
                else if (x.text < y.text) {
                    return -1;
                }
                return 0;
            });
            sortableItems.forEach((menuItem) => {
                if (menuItem.items.length > 0) {
                    menuItem.items = this.sortSideNavItemByName(menuItem.items);
                }
            });
        }
        const sortedItems = menuItems.filter((x) => x.isWorkspace);
        return sortedItems.concat(sortableItems);
    }
    // Private method
    hideNavigationItems(menuItems) {
        menuItems.forEach((menu) => {
            menu.visible = false;
            menu.hasAccess = false;
        });
        return menuItems;
    }
    deepCopy(menuItems) {
        return plainToClass(SideNavTreeInfo, JSON.parse(JSON.stringify(menuItems)));
    }
}
NavigationService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function NavigationService_Factory() { return new NavigationService(); }, token: NavigationService, providedIn: "root" });
